// Every player action that changes the sim, as data (PLAN-04 S1).
//
// A screen never mutates the world itself: it asks the store to perform one of
// these, the store hands it to `performAction` (sim/actionLog.ts), and that is
// the one place Veteran logs it. Because an action is plain data and
// `applyAction` is pure over (state, action), the server can replay the log
// and land on the same farm (PLAN-05).
//
// Anything an action needs from outside the sim — the calendar day a chore
// counts toward, say — rides in its args, so replay never reads the clock.

import { bus } from '../core/events';
import type { ChoreId, ConditionId, GameState } from '../core/types';
import { getConfig } from './config';
import { treatCondition } from './disease';
import { recoverEscapee, rollCatch } from './escape';
import { eggSalePrice } from './genetics';
import { isIncubating } from './incubation';
import { effectsOf, getItem } from './items';
import { feedTypeForStage } from './lifecycle';
import { recordChore } from './progression';
import { rollStream } from './league';
import { seasonPrice } from './season';
import { moveItem, placeItem, setFixture, type FixtureChoice } from './spots';
import { anchorCoops, stormAnchorDue } from './storm';
import { purchase, type PurchaseResult } from './store';
import {
  cleanLitter,
  dueChores,
  lockCoops,
  refillFeed,
  refillWater,
  summarizeFacilities,
} from './welfare';

export type FeedType = 'starter' | 'grower' | 'layer';
export type BagSize = 'small' | 'big';

export type SimAction =
  | { type: 'chore'; args: { chore: ChoreId; dayKey?: string } }
  | { type: 'catch'; args: { chickenId: string } }
  | { type: 'buy'; args: { itemId: string } }
  | { type: 'buyFeedBag'; args: { feedType: FeedType; size: BagSize; dayKey?: string } }
  | { type: 'inspect'; args: { chickenId: string } }
  | { type: 'handle'; args: { chickenId: string } }
  | { type: 'treat'; args: { chickenId: string; condition?: ConditionId } }
  | { type: 'placeItem'; args: { spotId: string; structureId: string } }
  | { type: 'moveItem'; args: { from: string; to: string } }
  | { type: 'setFixture'; args: FixtureChoice }
  | { type: 'setBrooderLamp'; args: { setting: number } };

export interface ActionResult {
  performed: boolean;
  /** i18n key of the message to show, when there is one. */
  messageKey: string | null;
  messageParams?: Record<string, string | number>;
  /** The fix lives in placing equipment, not in the store. */
  needsEquipment?: boolean;
  /** Eggits the action paid out (the daily checklist wage). */
  reward?: number;
  /** The ration that went into the feeder, for a feed purchase. */
  poured?: FeedType | null;
  /** How much stress a handle took off. */
  stressRelief?: number;
  /** A catch attempt: true when the bird is back in the run. */
  caught?: boolean;
  purchase?: PurchaseResult;
  /** Why a placement or fixture change was refused. */
  reason?: string;
}

/** Handling takes a little stress off (BHV-15). S8 replaces this with tameness. */
export const HANDLE_STRESS_RELIEF = 5;

export function applyAction(state: GameState, action: SimAction): ActionResult {
  switch (action.type) {
    case 'chore':
      return doChore(state, action.args.chore, action.args.dayKey);
    case 'catch':
      return doCatch(state, action.args.chickenId);
    case 'buy': {
      const result = purchase(state, action.args.itemId, rollStream(state, `buy:${state.tickCount}`));
      return { performed: result.ok, messageKey: null, purchase: result };
    }
    case 'buyFeedBag':
      return buyFeedBag(state, action.args.feedType, action.args.size, action.args.dayKey);
    case 'inspect': {
      // An inspection is how a hidden condition becomes acknowledged.
      const bird = state.flock.find((c) => c.id === action.args.chickenId);
      if (!bird) return { performed: false, messageKey: null };
      for (const condition of bird.conditions) condition.acknowledged = true;
      return { performed: true, messageKey: null };
    }
    case 'handle': {
      const bird = state.flock.find((c) => c.id === action.args.chickenId);
      if (!bird) return { performed: false, messageKey: null, stressRelief: 0 };
      bird.physiology.stress = Math.max(0, bird.physiology.stress - HANDLE_STRESS_RELIEF);
      return { performed: true, messageKey: null, stressRelief: HANDLE_STRESS_RELIEF };
    }
    case 'treat': {
      const bird = state.flock.find((c) => c.id === action.args.chickenId);
      const condition = action.args.condition ?? bird?.conditions[0]?.id;
      if (!bird || !condition) return { performed: false, messageKey: null };
      return { performed: treatCondition(bird, condition, state.simHour), messageKey: null };
    }
    case 'placeItem': {
      const result = placeItem(state, action.args.spotId, action.args.structureId);
      return result.ok
        ? { performed: true, messageKey: null }
        : { performed: false, messageKey: null, reason: result.reason };
    }
    case 'moveItem': {
      const result = moveItem(state, action.args.from, action.args.to);
      return result.ok
        ? { performed: true, messageKey: null }
        : { performed: false, messageKey: null, reason: result.reason };
    }
    case 'setFixture': {
      const result = setFixture(state, action.args);
      return result.ok
        ? { performed: true, messageKey: null }
        : { performed: false, messageKey: null, reason: result.reason };
    }
    case 'setBrooderLamp': {
      const brooder = state.structures.find((s) => effectsOf(s).brooder);
      if (!brooder) return { performed: false, messageKey: null, reason: 'no_brooder' };
      brooder.state.lampSetting = clampLamp(action.args.setting);
      return { performed: true, messageKey: null };
    }
  }
}

/** The lamp dial runs 0–100; `brooderTsiFor` reads it as a fraction of 40°F. */
function clampLamp(setting: number): number {
  if (!Number.isFinite(setting)) return 0;
  return Math.max(0, Math.min(100, Math.round(setting)));
}

// --- chores -----------------------------------------------------------------

/**
 * The "Quick" path through a chore: one tap, done. The rules are the sim's own
 * (`refillWater`, `refillFeed`, `cleanLitter`, `lockCoops`).
 */
function doChore(state: GameState, chore: ChoreId, dayKey?: string): ActionResult {
  const config = getConfig(state.veteran.enabled ? 'veteran' : 'normal');
  // A chore the player did unprompted is one the UI had not started nagging about.
  const prompted = dueChores(state, config).includes(chore);
  const result: ActionResult = {
    performed: false,
    messageKey: null,
    needsEquipment: false,
    reward: 0,
  };

  switch (chore) {
    case 'water':
      result.performed = refillWater(state) > 0;
      result.needsEquipment = !result.performed;
      break;
    case 'feed': {
      // The ration the youngest bird still needs — the age gate is the lesson,
      // so the chore never quietly serves the wrong bag. Alive birds only.
      const stages = state.flock.filter((c) => c.alive).map((c) => feedTypeForStage(c.stage));
      const wanted = stages.includes('starter')
        ? 'starter'
        : stages.includes('grower')
          ? 'grower'
          : 'layer';
      const bag = `feed_${wanted}`;
      result.performed = refillFeed(state, bag) > 0;
      if (!result.performed) {
        const noFeeder = summarizeFacilities(state).feeders.length === 0;
        result.needsEquipment = noFeeder;
        result.messageKey = noFeeder ? 'chore.noFeeder' : 'chore.wrongRation';
        result.messageParams = { bag: getItem(bag)?.name[localeOf(state)] ?? bag };
      }
      break;
    }
    case 'litter':
      result.performed = cleanLitter(state);
      break;
    case 'eggs':
      result.performed = collectEggs(state) > 0;
      break;
    case 'lock_coop':
      result.performed = lockCoops(state) > 0;
      break;
    case 'storm_anchor':
      // Refused outside a watch, so the chore bar's chip cannot tie down a coop
      // on a clear day and quietly tick a checklist item nobody was warned about.
      result.performed = stormAnchorDue(state) ? anchorCoops(state) > 0 : false;
      if (!result.performed) result.messageKey = 'chore.noStorm';
      break;
  }
  if (result.performed) {
    const recorded = recordChore(state, chore, { prompted, dayKey });
    result.reward = recorded.reward;
    if (recorded.checklistComplete) result.messageKey = 'chore.allDone';
  }
  if (!result.performed && !result.messageKey) result.messageKey = 'chore.noSupplies';
  return result;
}

/**
 * One attempt at getting a bird back over the fence (REFOCUS §12).
 *
 * The odds are the sim's own (`catchProbability`), drawn from a named stream
 * keyed on the bird and the tick, the way a purchase is — so the same log
 * replays into the same farm. A miss changes nothing, which is the point: the
 * player can try again, and the attempt costs a tap.
 */
function doCatch(state: GameState, chickenId: string): ActionResult {
  const bird = state.flock.find((c) => c.id === chickenId);
  if (!bird || bird.currentState !== 'ESCAPED') {
    return { performed: false, messageKey: 'catch.notEscaped', caught: false };
  }
  const config = getConfig(state.veteran.enabled ? 'veteran' : 'normal');
  const facilities = summarizeFacilities(state);
  const caught = rollCatch(
    bird,
    {
      fenceHeightInches: facilities.fenceHeightInches,
      gateUnlatched: facilities.anyDoorOpen,
      cooped: false,
    },
    config,
    rollStream(state, `catch:${bird.id}:${state.tickCount}`),
  );
  if (caught) recoverEscapee(bird);
  return {
    performed: true,
    messageKey: caught ? 'catch.caught' : 'catch.missed',
    messageParams: { name: bird.name },
    caught,
  };
}

function localeOf(state: GameState): 'en' | 'es' {
  return state.settings.locale === 'es' ? 'es' : 'en';
}

/** Collects every egg sitting in a nest box, crediting the sale. */
export function collectEggs(state: GameState): number {
  const facilities = summarizeFacilities(state);
  let collected = 0;
  for (const nest of facilities.nestBoxes) {
    for (const egg of nest.state.eggs ?? []) {
      // Eggs under the lamp are not breakfast — incubating clutches stay.
      if (isIncubating(egg)) continue;
      const price = eggSalePrice(egg.shell);
      state.eggits += price;
      collected++;
      bus.emit('EGG_COLLECTED', { eggId: egg.id, eggits: price });
    }
    nest.state.eggs = (nest.state.eggs ?? []).filter((egg) => isIncubating(egg));
  }
  state.progression.eggsCollected += collected;
  // Prompt collection is how a hen unlearns egg eating.
  if (collected > 0) {
    for (const chicken of state.flock) {
      if (!chicken.eggs.eggEatingHabit) continue;
      chicken.eggs.promptCollectionDays += 1;
      if (chicken.eggs.promptCollectionDays >= 5) {
        chicken.eggs.eggEatingHabit = false;
        chicken.eggs.promptCollectionDays = 0;
      }
    }
  }
  return collected;
}

// --- feed -------------------------------------------------------------------

/**
 * Buy a bag and pour it, in one tap. A wrong ration is allowed: REFOCUS §12a
 * is explicit that the consequence is real. The price is the season's.
 *
 * Pouring a bag off the shelf IS the feed chore, and it is the same chore the
 * Quick button does. It used to buy and pour without ever calling
 * `recordChore`, so the daily checklist could not be completed once the free
 * starter bags ran out and the wage — the game's only reliable income — was
 * unreachable for the rest of the season.
 */
function buyFeedBag(
  state: GameState,
  feedType: FeedType,
  size: BagSize,
  dayKey?: string,
): ActionResult {
  const itemId = `feed_${feedType}`;
  const result: ActionResult = { performed: false, messageKey: null, poured: null, reward: 0 };
  // Read before the pour: `dueChores` looks at what the feeder holds, and the
  // pour is what changes that. This is the same "was the HUD chip already
  // glowing" reading the Quick path takes.
  const prompted = feedChoreDue(state);
  const price = seasonPrice(itemId, getItem(itemId)?.price ?? 0);
  if (state.eggits < price) {
    result.messageKey = 'feed.notEnoughEggits';
    result.messageParams = { price };
    return result;
  }
  state.eggits -= price;
  state.inventory[itemId] = (state.inventory[itemId] ?? 0) + 1;
  if (refillFeed(state, itemId) === 0) {
    // No feeder placed. The bag is still bought, so the player is told where
    // it went rather than losing it silently — and the day is not credited,
    // because the birds still have nothing to eat.
    result.messageKey = 'feed.noFeeder';
    return result;
  }
  void size;
  result.performed = true;
  result.poured = feedType;
  // Recorded here, after the pour, so a purchase that could not be filled
  // never buys the player a paid day.
  const recorded = recordChore(state, 'feed', { prompted, dayKey });
  result.reward = recorded.reward;
  if (recorded.checklistComplete) result.messageKey = 'chore.allDone';
  return result;
}

/** Whether the feed chore is outstanding right now, for the unprompted metric. */
function feedChoreDue(state: GameState): boolean {
  const config = getConfig(state.veteran.enabled ? 'veteran' : 'normal');
  return dueChores(state, config).includes('feed');
}
